import React, { useCallback, useEffect, useState } from 'react';
import { CircleMarker, Polyline, Tooltip } from 'react-leaflet';
import { distanceMeters } from '../model/greenWave';

const MEASURE_COLOR = '#A1E430';
const MEASURE_EDGE = '#353A0A';

export function newMeasurementId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? `distance-${crypto.randomUUID()}`
    : `distance-${Date.now()}-${Math.round(Math.random() * 1e6)}`;
}

/**
 * Two-click ruler shared by the SEMAFIX overview map and the intersection
 * editor map: the first click pins the origin, the second one closes the
 * measure. Each map owns its own in-progress points while the completed
 * measures live in the page, so a distance can be taken from either view.
 */
export function useDistanceMeasurement({ map, active, onComplete }) {
  const [start, setStart] = useState(null);
  const [cursor, setCursor] = useState(null);

  const reset = useCallback(() => {
    setStart(null);
    setCursor(null);
  }, []);

  useEffect(() => {
    if (!active) reset();
  }, [active, reset]);

  useEffect(() => {
    if (!map || !active) return undefined;
    const handleClick = ({ latlng }) => {
      const point = { lat: latlng.lat, lng: latlng.lng };
      if (!start) {
        setStart(point);
        setCursor(point);
        return;
      }
      reset();
      onComplete?.({
        id: newMeasurementId(),
        from: start,
        to: point,
        distanceMeters: distanceMeters(start, point),
      });
    };
    const handleMove = ({ latlng }) => {
      if (start) setCursor({ lat: latlng.lat, lng: latlng.lng });
    };
    map.on('click', handleClick);
    map.on('mousemove', handleMove);
    return () => {
      map.off('click', handleClick);
      map.off('mousemove', handleMove);
    };
  }, [active, map, onComplete, reset, start]);

  return { start, cursor, reset };
}

/** Completed measures plus the rubber band of the one being taken. */
export default function MeasurementLayer({ measurements = [], start, cursor }) {
  return (
    <>
      {/* Manually typed distances have no geometry to draw. */}
      {measurements.map((measurement, index) => (measurement.from && measurement.to ? (
        <React.Fragment key={measurement.id}>
          <Polyline
            positions={[measurement.from, measurement.to]}
            pathOptions={{ color: MEASURE_COLOR, weight: 4, dashArray: '8 7' }}
          >
            <Tooltip permanent direction="center" className="semafix-measure-tooltip">
              {Math.round(measurement.distanceMeters)} m
            </Tooltip>
          </Polyline>
          <CircleMarker
            center={measurement.from}
            radius={4}
            pathOptions={{ color: MEASURE_EDGE, fillColor: MEASURE_COLOR, fillOpacity: 1 }}
          />
          <CircleMarker
            center={measurement.to}
            radius={4}
            pathOptions={{ color: MEASURE_EDGE, fillColor: MEASURE_COLOR, fillOpacity: 1 }}
          >
            <Tooltip direction="top">Misura {index + 1}</Tooltip>
          </CircleMarker>
        </React.Fragment>
      ) : null))}
      {start && cursor && (
        <Polyline
          positions={[start, cursor]}
          pathOptions={{ color: MEASURE_COLOR, weight: 3, dashArray: '5 6', opacity: 0.8 }}
        />
      )}
    </>
  );
}
